import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useAction, useQuery, useMutation } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Id } from '../../../convex/_generated/dataModel'
import {
  Image as ImageIcon,
  Film,
  Check,
  X,
  Loader2,
  Clock,
  ChevronDown,
  ChevronRight,
  Undo2,
  Redo2,
  Eye,
  RefreshCw,
  AlertCircle,
  Copy,
  User,
  Bot,
  Wrench,
  MessageSquare,
  Paperclip,
  Hash,
} from 'lucide-react'
import { formatToolInline, formatToolInputFallbackDetail } from '@/lib/config/toolDisplay'
import { useProjectStore } from '@/lib/projectStore'

type HistoryEntry = { id: string; label: string; source: 'user' | 'agent'; toolName: string; undone: boolean; undoable?: boolean; status?: 'ok' | 'failed'; invalidatedAt?: number; createdAt: number }

interface ToolCall {
  id: string
  name: string
  input?: Record<string, unknown> | null
  status?: 'running' | 'completed' | 'failed'
  result?: {
    success?: boolean
    data?: unknown
    error?: string
    input?: unknown
  }
}

interface ToolResult {
  toolCallId: string
  result: unknown
}

interface WorkflowMessage {
  _id?: string
  id?: string
  role: string
  content: string
  toolCalls?: ToolCall[]
  toolResults?: ToolResult[]
  isStreaming?: boolean
  createdAt?: number
  timestamp?: number
}

interface ObservabilityAttachment {
  source?: { type: string; url?: string }
  type?: string
  text?: string
}

interface ObservabilityToolCall {
  id: string
  name: string
  input?: Record<string, unknown>
}

interface ObservabilityToolResult {
  toolCallId: string
  result?: unknown
}

interface ObservabilitySourceMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  attachments?: ObservabilityAttachment[]
  toolCalls?: ObservabilityToolCall[]
  toolResults?: ObservabilityToolResult[]
  isStreaming?: boolean
  aborted?: boolean
  createdAt: number
}

interface AnthropicContentBlock {
  type: string
  text?: string
  id?: string
  name?: string
  input?: unknown
  tool_use_id?: string
  content?: string | AnthropicContentBlock[]
  source?: { type: string; media_type?: string; data?: string; url?: string }
  _mergedResult?: AnthropicContentBlock
}

interface AnthropicReplayMessage {
  role: 'user' | 'assistant'
  content: string | AnthropicContentBlock[]
  _hidden?: boolean
}

function mergeToolResults(messages: AnthropicReplayMessage[]): AnthropicReplayMessage[] {
  const result = messages.map(m => ({ ...m, content: typeof m.content === 'string' ? m.content : m.content.map(b => ({ ...b })) }))

  for (let i = 0; i < result.length; i++) {
    const msg = result[i]
    if (msg.role !== 'user' || typeof msg.content === 'string') continue

    const blocks = msg.content
    const isAllToolResults = blocks.length > 0 && blocks.every(b => b.type === 'tool_result')
    if (!isAllToolResults) continue

    // Find preceding assistant message to attach results to
    for (let j = i - 1; j >= 0; j--) {
      if (result[j].role === 'assistant' && typeof result[j].content !== 'string') {
        const assistantBlocks = result[j].content as AnthropicContentBlock[]
        for (const resultBlock of blocks) {
          const toolUse = assistantBlocks.find(b => b.type === 'tool_use' && b.id === resultBlock.tool_use_id)
          if (toolUse) toolUse._mergedResult = resultBlock
        }
        msg._hidden = true
        break
      }
    }
  }

  return result.filter(m => !m._hidden)
}

interface ToolDefinition {
  name: string
  type?: string
  description?: string
  input_schema?: {
    type?: string
    properties?: Record<string, {
      type?: string | string[]
      description?: string
      enum?: string[]
      items?: { type?: string; properties?: Record<string, { type?: string; description?: string; enum?: string[] }>; required?: string[] }
      maxItems?: number
    }>
    required?: string[]
  }
  max_uses?: number
}

interface ObservabilitySnapshot {
  generatedAt: number
  model: string
  systemPrompt: string
  contextMessage: string
  replayMessages: AnthropicReplayMessage[]
  sourceMessages: ObservabilitySourceMessage[]
  tools?: ToolDefinition[]
  stats: {
    sourceMessageCount: number
    replayMessageCount: number
    clipCount: number
  }
}

interface WorkflowClip {
  _id: string
  title?: string
  description?: string
  videoPrompt?: string
  visualDescription?: string // Legacy field
  promptSummary?: string
  duration: number
  orderIndex: number
  storyboardImageUrl?: string
  videoUrl?: string
  videoStatus: string
  metadata?: Record<string, unknown>
}

interface ActivityEvent {
  id: string
  type: 'tool_start' | 'tool_end' | 'clip_created' | 'image_started' | 'image_ready' | 'video_started' | 'video_completed' | 'video_failed'
  timestamp: Date
  title: string
  subtitle?: string
  clipIndex?: number
  status: 'running' | 'completed' | 'failed'
  toolName?: string
  toolInput?: Record<string, unknown>
  toolResult?: unknown
  toolError?: string
}

interface ActivityTimelineProps {
  messages: WorkflowMessage[]
  clips: WorkflowClip[]
  workflowId?: Id<"workflows">
  isAdmin?: boolean
}

function getObservabilityErrorMessage(error: unknown): string {
  const rawMessage = error instanceof Error ? error.message : 'Failed to load observability'
  const requestIdMatch = rawMessage.match(/Request ID:\\s*([a-f0-9]+)/i)
  const requestIdSuffix = requestIdMatch ? ` (Request ID: ${requestIdMatch[1]})` : ''
  if (rawMessage.includes("Could not find public function for 'workflowAgent:getAgentObservability'")) {
    return `Observability backend function is not deployed yet. Run \`npx convex deploy -y\` and refresh.${requestIdSuffix}`
  }
  if (rawMessage.includes('Server Error Called by client')) {
    return `Observability request failed on the backend. If this started after a UI release, deploy Convex functions and refresh.${requestIdSuffix}`
  }
  return rawMessage
}

function getToolDisplay(toolName: string, input?: Record<string, unknown>) {
  const { name, detail, icon } = formatToolInline(toolName, input || null)
  return { name, detail, icon }
}

function formatRelativeTime(date: Date): string {
  if (Number.isNaN(date.getTime())) return 'unknown'
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSecs = Math.floor(diffMs / 1000)

  if (diffSecs < 5) return 'just now'
  if (diffSecs < 60) return `${diffSecs}s ago`

  const diffMins = Math.floor(diffSecs / 60)
  if (diffMins < 60) return `${diffMins}m ago`

  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`

  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
}

function toNonEmptyRecord(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined
  const record = value as Record<string, unknown>
  return Object.keys(record).length > 0 ? record : undefined
}

function resolveEventToolInput(
  toolInput: unknown,
  toolResult: { success?: boolean; error?: string; data?: unknown; input?: unknown } | undefined
): Record<string, unknown> | undefined {
  const directInput = toNonEmptyRecord(toolInput)
  if (directInput) return directInput

  const resultInput = toNonEmptyRecord(toolResult?.input)
  if (resultInput) return resultInput

  return undefined
}

function buildActivityEvents(
  messages: WorkflowMessage[],
  clips: WorkflowClip[]
): ActivityEvent[] {
  const events: ActivityEvent[] = []
  const clipMap = new Map(clips.map((s) => [s._id, { clip: s }]))

  // Extract tool call events from messages
  for (let messageIndex = 0; messageIndex < messages.length; messageIndex++) {
    const message = messages[messageIndex]
    if (!message.toolCalls?.length) continue

    const messageTime =
      (typeof message.createdAt === 'number' ? message.createdAt : undefined) ??
      (typeof message.timestamp === 'number' ? message.timestamp : undefined) ??
      (Date.now() - (messages.length - messageIndex) * 1000)

    for (let i = 0; i < message.toolCalls.length; i++) {
      const toolCall = message.toolCalls[i]
      const rawInput = toolCall.input
      const toolDisplay = getToolDisplay(toolCall.name, toNonEmptyRecord(rawInput))
      const toolResultItem = message.toolResults?.find(r => r.toolCallId === toolCall.id)
      const result = (toolResultItem?.result ?? toolCall.result) as { success?: boolean; error?: string; data?: unknown; input?: unknown } | undefined
      const input = resolveEventToolInput(rawInput, result)
      const isSuccess = result && result.success !== false && !result.error
      const isFailed = result && (result.success === false || !!result.error)
      const isRunning = toolCall.status === 'running' || (!toolResultItem && !isFailed)

      // Get clip info if tool is clip-related
      let clipIndex: number | undefined
      const subtitleParts: string[] = []

      if (input?.clip_id) {
        const clipInfo = clipMap.get(input.clip_id as string)
        if (clipInfo) {
          clipIndex = clipInfo.clip.orderIndex + 1
          subtitleParts.push(`Clip ${clipIndex}`)
        }
      }

      // Add detail from centralized config if available
      const detail = toolDisplay.detail || formatToolInputFallbackDetail(input || null, undefined, false)
      if (detail) subtitleParts.push(detail)

      const subtitle = subtitleParts.length > 0 ? subtitleParts.join(' · ') : undefined

      events.push({
        id: toolCall.id,
        type: toolResultItem ? 'tool_end' : 'tool_start',
        timestamp: new Date(messageTime + i),
        title: toolDisplay.name,
        subtitle,
        clipIndex,
        status: isFailed || toolCall.status === 'failed'
          ? 'failed'
          : isSuccess || toolCall.status === 'completed'
            ? 'completed'
            : isRunning || message.isStreaming
              ? 'running'
              : 'completed',
        toolName: toolCall.name,
        toolInput: input,
        toolResult: result?.data ?? result,
        toolError: result?.error,
      })
    }
  }

  // Extract clip status events from clip metadata
  for (const clip of clips) {
    const clipInfo = clipMap.get(clip._id)
    const clipIndex = clipInfo ? clipInfo.clip.orderIndex + 1 : undefined
    const metadata = clip.metadata || {}

    // Video rendering in progress
    if (clip.videoStatus === 'generating_video') {
      const startedAt = metadata.videoGenerationStartedAt as number | undefined
      events.push({
        id: `${clip._id}-video-start`,
        type: 'video_started',
        timestamp: new Date(startedAt || Date.now()),
        title: 'Video Rendering',
        subtitle: `Clip ${clipIndex}`,
        clipIndex,
        status: 'running',
      })
    }

    // Image generation in progress
    if (clip.videoStatus === 'generating_image') {
      events.push({
        id: `${clip._id}-image-start`,
        type: 'image_started',
        timestamp: new Date(),
        title: 'Image Generation',
        subtitle: `Clip ${clipIndex}`,
        clipIndex,
        status: 'running',
      })
    }

    // Video completed
    if (clip.videoUrl && clip.videoStatus === 'completed') {
      const completedAt = metadata.videoCompletedAt as number | undefined
      events.push({
        id: `${clip._id}-video-complete`,
        type: 'video_completed',
        timestamp: new Date(completedAt || Date.now() - 1000),
        title: 'Video Ready',
        subtitle: `Clip ${clipIndex}`,
        clipIndex,
        status: 'completed',
      })
    }

    // Video failed
    if (clip.videoStatus === 'failed') {
      events.push({
        id: `${clip._id}-video-fail`,
        type: 'video_failed',
        timestamp: new Date(),
        title: 'Video Failed',
        subtitle: `Clip ${clipIndex}${metadata.lastError ? `: ${metadata.lastError}` : ''}`,
        clipIndex,
        status: 'failed',
      })
    }
  }

  // Sort by timestamp descending (most recent first)
  events.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())

  return events
}

function EventIcon({ event }: { event: ActivityEvent }) {
  const baseClass = 'shrink-0'

  if (event.status === 'running') {
    return <Loader2 size={14} className={`${baseClass} text-primary animate-spin`} />
  }

  if (event.status === 'failed') {
    return <X size={14} className={`${baseClass} text-destructive`} />
  }

  switch (event.type) {
    case 'image_started':
    case 'image_ready':
      return <ImageIcon size={14} className={`${baseClass} text-purple-500`} />
    case 'video_started':
    case 'video_completed':
      return <Film size={14} className={`${baseClass} text-success`} />
    case 'video_failed':
      return <X size={14} className={`${baseClass} text-destructive`} />
    default:
      if (event.toolName) {
        const display = getToolDisplay(event.toolName, event.toolInput)
        return <span className={`${baseClass} text-muted-foreground`}>{display.icon}</span>
      }
      return <Check size={14} className={`${baseClass} text-success`} />
  }
}

function formatJson(obj: unknown): string {
  if (obj === null || obj === undefined) return ''
  try {
    return JSON.stringify(obj, null, 2)
  } catch {
    return String(obj)
  }
}

function ToolDetails({ event }: { event: ActivityEvent }) {
  const hasInput = event.toolInput && Object.keys(event.toolInput).length > 0
  const hasParams = !!event.toolName
  const hasResult = event.toolResult !== undefined
  const hasError = !!event.toolError

  if (!hasParams && !hasInput && !hasResult && !hasError) {
    return null
  }

  return (
    <div className="mt-2 space-y-2">
      {hasParams && (
        <div>
          <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1">Params</div>
          <pre className="text-[11px] text-secondary-foreground bg-muted p-2 rounded border border-border overflow-x-auto whitespace-pre-wrap break-all">
            {formatJson(event.toolInput ?? {})}
          </pre>
        </div>
      )}
      {hasError && (
        <div>
          <div className="text-[10px] font-medium text-destructive/70 uppercase tracking-wide mb-1">Error</div>
          <pre className="text-[11px] text-destructive bg-destructive/10 p-2 rounded border border-destructive/20 overflow-x-auto whitespace-pre-wrap break-all">
            {event.toolError}
          </pre>
        </div>
      )}
      {hasResult && !hasError && (
        <div>
          <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1">Result</div>
          <pre className="text-[11px] text-success bg-success/10 p-2 rounded border border-success/20 overflow-x-auto whitespace-pre-wrap break-all">
            {formatJson(event.toolResult)}
          </pre>
        </div>
      )}
    </div>
  )
}

function ActivityEventItem({ event }: { event: ActivityEvent }) {
  const [expanded, setExpanded] = useState(false)
  const hasInput = !!event.toolInput && Object.keys(event.toolInput).length > 0
  const hasToolParams = !!event.toolName
  const hasResult = event.toolResult !== undefined
  const hasError = !!event.toolError
  const hasDetails = hasToolParams || hasInput || hasResult || hasError

  return (
    <div className="bg-card">
      <button
        onClick={() => hasDetails && setExpanded(!expanded)}
        className={`w-full flex items-start gap-2 py-2.5 px-3 text-left transition-colors ${
          hasDetails ? 'hover:bg-accent cursor-pointer' : 'cursor-default'
        }`}
        disabled={!hasDetails}
      >
        {hasDetails ? (
          <div className="mt-0.5 text-muted-foreground">
            {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </div>
        ) : (
          <div className="w-3" />
        )}
        <div className="mt-0.5">
          <EventIcon event={event} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium ${
              event.status === 'failed' ? 'text-destructive' :
              event.status === 'running' ? 'text-primary' : 'text-secondary-foreground'
            }`}>
              {event.title}
            </span>
            {event.status === 'running' && (
              <span className="text-[10px] text-primary bg-primary/10 px-1.5 py-0.5 rounded">
                In Progress
              </span>
            )}
          </div>
          {event.subtitle && (
            <p className="text-xs text-muted-foreground mt-0.5">{event.subtitle}</p>
          )}
        </div>
        <span className="text-[11px] text-muted-foreground whitespace-nowrap mt-0.5">
          {formatRelativeTime(event.timestamp)}
        </span>
      </button>
      {expanded && hasDetails && (
        <div className="px-3 pb-3 pl-8">
          <ToolDetails event={event} />
        </div>
      )}
    </div>
  )
}

// ─── Observability Panel Components ───────────────────────────────────────────

function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text).catch(() => {
      fallbackCopy(text)
    })
  }
  fallbackCopy(text)
  return Promise.resolve()
}

function fallbackCopy(text: string) {
  const el = document.createElement('textarea')
  el.value = text
  el.style.cssText = 'position:fixed;opacity:0;pointer-events:none'
  document.body.appendChild(el)
  el.select()
  document.execCommand('copy')
  document.body.removeChild(el)
}

function CopyButton({ text, className = '' }: { text: string; className?: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      onClick={async (e) => {
        e.stopPropagation()
        await copyToClipboard(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      }}
      className={`p-1 rounded transition-colors ${copied ? 'text-success' : 'text-muted-foreground hover:text-secondary-foreground hover:bg-muted'} ${className}`}
      title={copied ? 'Copied' : 'Copy to clipboard'}
    >
      {copied ? <Check size={11} /> : <Copy size={11} />}
    </button>
  )
}

type ExpandSignal = { expand: boolean; v: number }
const ExpandAllCtx = createContext<ExpandSignal>({ expand: false, v: 0 })

function CollapsibleSection({
  title,
  badge,
  children,
  defaultOpen = false,
  copyText,
}: {
  title: string
  badge?: string | number
  children: React.ReactNode
  defaultOpen?: boolean
  copyText?: string
}) {
  const [open, setOpen] = useState(defaultOpen)
  const signal = useContext(ExpandAllCtx)
  const lastV = useRef(0)
  useEffect(() => {
    if (signal.v > lastV.current) {
      lastV.current = signal.v
      setOpen(signal.expand)
    }
  }, [signal])
  return (
    <div className="border border-amber-200/60 rounded-md overflow-hidden bg-white/60">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-1.5 px-2.5 py-1.5 text-left hover:bg-amber-50/50 transition-colors"
      >
        <span className="text-amber-600">
          {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
        </span>
        <span className="text-[10px] font-semibold text-amber-800 uppercase tracking-wide flex-1">{title}</span>
        {badge !== undefined && (
          <span className="text-[9px] font-medium text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded-full">{badge}</span>
        )}
        {copyText && <CopyButton text={copyText} />}
      </button>
      {open && <div className="border-t border-amber-100">{children}</div>}
    </div>
  )
}

function SystemPromptView({ prompt }: { prompt: string }) {
  const sections = useMemo(() => {
    const parts: { heading: string; content: string }[] = []
    const lines = prompt.split('\n')
    let currentHeading = 'Preamble'
    let currentLines: string[] = []

    for (const line of lines) {
      const headingMatch = line.match(/^#{1,3}\s+(.+)/)
      if (headingMatch) {
        if (currentLines.length > 0) {
          parts.push({ heading: currentHeading, content: currentLines.join('\n').trim() })
        }
        currentHeading = headingMatch[1]
        currentLines = []
      } else {
        currentLines.push(line)
      }
    }
    if (currentLines.length > 0) {
      parts.push({ heading: currentHeading, content: currentLines.join('\n').trim() })
    }
    return parts.filter(p => p.content.length > 0)
  }, [prompt])

  return (
    <div className="divide-y divide-amber-100/60">
      {sections.map((section, i) => (
        <CollapsibleSection key={i} title={section.heading} defaultOpen={i === 0} copyText={section.content}>
          <pre className="text-[10.5px] leading-relaxed text-secondary-foreground p-2.5 whitespace-pre-wrap wrap-break-word font-mono">
            {section.content}
          </pre>
        </CollapsibleSection>
      ))}
    </div>
  )
}

function ContextMessageView({ context }: { context: string }) {
  const sections = useMemo(() => {
    const parts: { label: string; content: string; type: 'text' | 'url' | 'json' }[] = []
    const lines = context.split('\n')
    let currentLabel = ''
    let currentLines: string[] = []

    for (const line of lines) {
      const headingMatch = line.match(/^#{1,3}\s+(.+)/)
      if (headingMatch) {
        if (currentLines.length > 0 && currentLabel) {
          const content = currentLines.join('\n').trim()
          parts.push({ label: currentLabel, content, type: content.startsWith('http') ? 'url' : 'text' })
        }
        currentLabel = headingMatch[1]
        currentLines = []
      } else {
        currentLines.push(line)
      }
    }
    if (currentLines.length > 0) {
      const content = currentLines.join('\n').trim()
      if (content) {
        parts.push({ label: currentLabel || 'Context', content, type: 'text' })
      }
    }
    return parts
  }, [context])

  if (sections.length === 0) {
    return <pre className="text-[10.5px] text-muted-foreground p-2.5 italic">Empty context</pre>
  }

  return (
    <div className="p-2.5 space-y-2">
      {sections.map((section, i) => (
        <div key={i}>
          <div className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider mb-0.5">{section.label}</div>
          <pre className="text-[10.5px] leading-relaxed text-secondary-foreground whitespace-pre-wrap wrap-break-word font-mono">
            {section.content}
          </pre>
        </div>
      ))}
    </div>
  )
}

function truncateText(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text
  return text.slice(0, maxLen) + '...'
}

function ReplayMessageItem({ msg, index }: { msg: AnthropicReplayMessage; index: number }) {
  const [expanded, setExpanded] = useState(false)
  const isUser = msg.role === 'user'
  const isAssistant = msg.role === 'assistant'

  const blocks: AnthropicContentBlock[] = typeof msg.content === 'string'
    ? [{ type: 'text', text: msg.content }]
    : (msg.content || [])

  const textBlocks = blocks.filter(b => b.type === 'text')
  const toolUseBlocks = blocks.filter(b => b.type === 'tool_use')
  const toolResultBlocks = blocks.filter(b => b.type === 'tool_result')
  const imageBlocks = blocks.filter(b => b.type === 'image')

  const previewText = textBlocks.map(b => b.text || '').join(' ').trim()

  return (
    <div className={`border-l-2 ${isUser ? 'border-l-blue-300' : isAssistant ? 'border-l-emerald-300' : 'border-l-border'}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-start gap-2 px-2.5 py-2 text-left hover:bg-accent/50 transition-colors"
      >
        <span className="mt-0.5 shrink-0">
          {isUser ? <User size={11} className="text-blue-500" /> : <Bot size={11} className="text-emerald-500" />}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className={`text-[10px] font-semibold uppercase ${isUser ? 'text-blue-600' : 'text-emerald-600'}`}>
              {msg.role}
            </span>
            <span className="text-[9px] text-muted-foreground">#{index + 1}</span>
            {toolUseBlocks.length > 0 && (
              <span className="text-[9px] text-violet-600 bg-violet-50 px-1 py-0.5 rounded flex items-center gap-0.5">
                <Wrench size={8} /> {toolUseBlocks.length}
              </span>
            )}
            {toolResultBlocks.length > 0 && (
              <span className="text-[9px] text-orange-600 bg-orange-50 px-1 py-0.5 rounded flex items-center gap-0.5">
                <Hash size={8} /> {toolResultBlocks.length}
              </span>
            )}
            {imageBlocks.length > 0 && (
              <span className="text-[9px] text-purple-600 bg-purple-50 px-1 py-0.5 rounded flex items-center gap-0.5">
                <ImageIcon size={8} /> {imageBlocks.length}
              </span>
            )}
          </div>
          {previewText && (
            <p className="text-[10.5px] text-secondary-foreground mt-0.5 line-clamp-2">{truncateText(previewText, 200)}</p>
          )}
          {!previewText && toolUseBlocks.length > 0 && (
            <p className="text-[10.5px] text-violet-600 mt-0.5">
              {toolUseBlocks.map(b => b.name).join(', ')}
            </p>
          )}
        </div>
        <span className="text-muted-foreground mt-0.5">
          {expanded ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
        </span>
      </button>
      {expanded && (
        <div className="px-2.5 pb-2.5 space-y-1.5">
          {blocks.map((block, bi) => (
            <ReplayContentBlock key={bi} block={block} />
          ))}
        </div>
      )}
    </div>
  )
}

function ReplayContentBlock({ block }: { block: AnthropicContentBlock }) {
  const [showFull, setShowFull] = useState(false)

  if (block.type === 'text') {
    const text = block.text || ''
    const isLong = text.length > 500
    return (
      <div>
        <pre className="text-[10px] text-secondary-foreground bg-secondary rounded p-2 whitespace-pre-wrap wrap-break-word font-mono">
          {showFull ? text : truncateText(text, 500)}
        </pre>
        {isLong && (
          <button onClick={() => setShowFull(!showFull)} className="text-[9px] text-primary hover:text-primary/80 mt-0.5">
            {showFull ? 'Show less' : `Show all (${text.length.toLocaleString()} chars)`}
          </button>
        )}
      </div>
    )
  }

  if (block.type === 'tool_use') {
    const resultBlock = block._mergedResult
    const resultContent = resultBlock
      ? (typeof resultBlock.content === 'string'
          ? resultBlock.content
          : Array.isArray(resultBlock.content)
            ? resultBlock.content.map(c => c.text || JSON.stringify(c)).join('\n')
            : '')
      : null
    return (
      <div className="rounded border border-violet-200 bg-violet-50/50 overflow-hidden">
        <div className="flex items-center gap-1.5 px-2 py-1 bg-violet-100/50">
          <Wrench size={10} className="text-violet-600" />
          <span className="text-[10px] font-semibold text-violet-700">{block.name}</span>
          <span className="text-[9px] text-violet-400 ml-auto font-mono">{block.id}</span>
        </div>
        <pre className="text-[10px] text-secondary-foreground p-2 whitespace-pre-wrap wrap-break-word font-mono">
          {formatJson(block.input)}
        </pre>
        {resultContent !== null && (
          <div className="border-t border-violet-200">
            <div className="flex items-center gap-1.5 px-2 py-1 bg-orange-50/50">
              <Hash size={9} className="text-orange-500" />
              <span className="text-[9px] font-semibold text-orange-600">Result</span>
            </div>
            <pre className="text-[10px] text-secondary-foreground p-2 whitespace-pre-wrap wrap-break-word font-mono">
              {resultContent || '(empty)'}
            </pre>
          </div>
        )}
      </div>
    )
  }

  if (block.type === 'tool_result') {
    const content = typeof block.content === 'string'
      ? block.content
      : Array.isArray(block.content)
        ? block.content.map(c => c.text || JSON.stringify(c)).join('\n')
        : ''
    return (
      <div className="rounded border border-orange-200 bg-orange-50/50 overflow-hidden">
        <div className="flex items-center gap-1.5 px-2 py-1 bg-orange-100/50">
          <Hash size={10} className="text-orange-600" />
          <span className="text-[10px] font-semibold text-orange-700">Result</span>
          <span className="text-[9px] text-orange-400 ml-auto font-mono">{block.tool_use_id}</span>
        </div>
        <pre className="text-[10px] text-secondary-foreground p-2 whitespace-pre-wrap wrap-break-word font-mono">
          {content || '(empty)'}
        </pre>
      </div>
    )
  }

  if (block.type === 'image') {
    return (
      <div className="flex items-center gap-1.5 px-2 py-1.5 bg-purple-50 rounded border border-purple-200">
        <ImageIcon size={10} className="text-purple-600" />
        <span className="text-[10px] text-purple-700">
          Image ({block.source?.type || 'unknown'})
          {block.source?.url && <span className="text-[9px] text-purple-400 ml-1">{truncateText(block.source.url, 60)}</span>}
        </span>
      </div>
    )
  }

  return (
    <pre className="text-[10px] text-muted-foreground bg-secondary rounded p-2 whitespace-pre-wrap wrap-break-word font-mono">
      {formatJson(block)}
    </pre>
  )
}

function SourceMessageItem({ msg, index }: { msg: ObservabilitySourceMessage; index: number }) {
  const [expanded, setExpanded] = useState(false)
  const isUser = msg.role === 'user'
  const isSystem = msg.role === 'system'

  const hasToolCalls = (msg.toolCalls?.length ?? 0) > 0
  const hasToolResults = (msg.toolResults?.length ?? 0) > 0
  const hasAttachments = (msg.attachments?.length ?? 0) > 0
  const hasDetails = hasToolCalls || hasToolResults || hasAttachments || msg.content.length > 200

  const timeStr = new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })

  return (
    <div className={`border-l-2 ${isSystem ? 'border-l-amber-300' : isUser ? 'border-l-blue-300' : 'border-l-emerald-300'}`}>
      <button
        onClick={() => hasDetails && setExpanded(!expanded)}
        className={`w-full flex items-start gap-2 px-2.5 py-2 text-left transition-colors ${hasDetails ? 'hover:bg-accent/50 cursor-pointer' : 'cursor-default'}`}
        disabled={!hasDetails}
      >
        <span className="mt-0.5 shrink-0">
          {isSystem ? <MessageSquare size={11} className="text-amber-500" /> :
           isUser ? <User size={11} className="text-blue-500" /> :
           <Bot size={11} className="text-emerald-500" />}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`text-[10px] font-semibold uppercase ${
              isSystem ? 'text-amber-600' : isUser ? 'text-blue-600' : 'text-emerald-600'
            }`}>
              {msg.role}
            </span>
            <span className="text-[9px] text-muted-foreground">#{index + 1}</span>
            <span className="text-[9px] text-muted-foreground">{timeStr}</span>
            {msg.isStreaming && (
              <span className="text-[9px] text-blue-500 bg-blue-50 px-1 py-0.5 rounded">streaming</span>
            )}
            {msg.aborted && (
              <span className="text-[9px] text-red-500 bg-red-50 px-1 py-0.5 rounded">aborted</span>
            )}
            {hasToolCalls && (
              <span className="text-[9px] text-violet-600 bg-violet-50 px-1 py-0.5 rounded flex items-center gap-0.5">
                <Wrench size={8} /> {msg.toolCalls!.length}
              </span>
            )}
            {hasAttachments && (
              <span className="text-[9px] text-purple-600 bg-purple-50 px-1 py-0.5 rounded flex items-center gap-0.5">
                <Paperclip size={8} /> {msg.attachments!.length}
              </span>
            )}
          </div>
          <p className="text-[10.5px] text-secondary-foreground mt-0.5 line-clamp-2">
            {truncateText(msg.content, 200) || '(no text content)'}
          </p>
        </div>
        {hasDetails && (
          <span className="text-muted-foreground mt-0.5">
            {expanded ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
          </span>
        )}
      </button>
      {expanded && (
        <div className="px-2.5 pb-2.5 space-y-1.5">
          {msg.content.length > 200 && (
            <pre className="text-[10px] text-secondary-foreground bg-secondary rounded p-2 whitespace-pre-wrap wrap-break-word font-mono">
              {msg.content}
            </pre>
          )}
          {msg.toolCalls?.map((tc) => (
            <div key={tc.id} className="rounded border border-violet-200 bg-violet-50/50 overflow-hidden">
              <div className="flex items-center gap-1.5 px-2 py-1 bg-violet-100/50">
                <Wrench size={10} className="text-violet-600" />
                <span className="text-[10px] font-semibold text-violet-700">{tc.name}</span>
                <span className="text-[9px] text-violet-400 ml-auto font-mono">{tc.id?.slice(0, 12)}</span>
              </div>
              {tc.input && Object.keys(tc.input).length > 0 && (
                <pre className="text-[10px] text-secondary-foreground p-2 whitespace-pre-wrap wrap-break-word font-mono">
                  {formatJson(tc.input)}
                </pre>
              )}
            </div>
          ))}
          {msg.toolResults?.map((tr, ri) => (
            <div key={ri} className="rounded border border-orange-200 bg-orange-50/50 overflow-hidden">
              <div className="flex items-center gap-1.5 px-2 py-1 bg-orange-100/50">
                <Hash size={10} className="text-orange-600" />
                <span className="text-[10px] font-semibold text-orange-700">Result</span>
                <span className="text-[9px] text-orange-400 ml-auto font-mono">{tr.toolCallId?.slice(0, 12)}</span>
              </div>
              <pre className="text-[10px] text-secondary-foreground p-2 whitespace-pre-wrap wrap-break-word font-mono">
                {formatJson(tr.result)}
              </pre>
            </div>
          ))}
          {msg.attachments?.map((att, ai) => (
            <div key={ai} className="flex items-center gap-1.5 px-2 py-1.5 bg-purple-50 rounded border border-purple-200">
              <Paperclip size={10} className="text-purple-600" />
              <span className="text-[10px] text-purple-700">
                {att.type || 'attachment'}
                {att.source?.url && <span className="text-[9px] text-purple-400 ml-1">{truncateText(att.source.url, 60)}</span>}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function ReplayMessagesSection({ replayMessages }: { replayMessages: AnthropicReplayMessage[] }) {
  const merged = useMemo(() => mergeToolResults(replayMessages), [replayMessages])
  return (
    <CollapsibleSection
      title="Replay Messages"
      badge={merged.length}
      copyText={JSON.stringify(replayMessages, null, 2)}
    >
      <div className="divide-y divide-border">
        {merged.map((msg, i) => (
          <ReplayMessageItem key={i} msg={msg} index={i} />
        ))}
        {merged.length === 0 && (
          <div className="text-[10.5px] text-muted-foreground italic p-2.5">No replay messages</div>
        )}
      </div>
    </CollapsibleSection>
  )
}

function ToolParamRow({ name, prop, required }: { name: string; prop: Record<string, unknown>; required: boolean }) {
  const typeStr = prop.type as string || 'any'
  const enumVals = prop.enum as string[] | undefined
  const desc = prop.description as string | undefined
  const items = prop.items as Record<string, unknown> | undefined
  const nestedProps = items?.properties as Record<string, Record<string, unknown>> | undefined

  return (
    <>
      <div className="flex items-baseline gap-1.5 text-[10px] py-0.5">
        <code className="font-mono text-violet-700 shrink-0">{name}</code>
        {required && <span className="text-[8px] text-red-400 font-bold shrink-0">req</span>}
        <span className="text-muted-foreground shrink-0 text-[9px]">
          {typeStr === 'array' && items ? `${(items.type as string) || 'object'}[]` : typeStr}
        </span>
        {enumVals && (
          <span className="text-[9px] text-amber-600 font-mono truncate">
            {enumVals.join(' | ')}
          </span>
        )}
      </div>
      {desc && <div className="text-[9.5px] text-muted-foreground pl-3 pb-1 leading-snug">{desc}</div>}
      {nestedProps && (
        <div className="pl-4 border-l border-border ml-1 mb-1">
          {Object.entries(nestedProps).map(([k, v]) => (
            <div key={k} className="flex items-baseline gap-1.5 text-[9.5px] py-0.5">
              <code className="font-mono text-secondary-foreground shrink-0">{k}</code>
              <span className="text-muted-foreground text-[9px]">{(v as Record<string, unknown>).type as string || 'any'}</span>
            </div>
          ))}
        </div>
      )}
    </>
  )
}

function ToolItemView({ tool }: { tool: ToolDefinition }) {
  const schema = tool.input_schema
  const properties = schema?.properties || {}
  const required = new Set(schema?.required || [])
  const propEntries = Object.entries(properties)
  const paramCount = propEntries.length

  const badge = tool.type ? 'built-in' : paramCount > 0 ? `${paramCount}p` : undefined

  return (
    <CollapsibleSection
      title={tool.name}
      badge={badge}
      copyText={JSON.stringify(tool, null, 2)}
    >
      <div className="px-2.5 py-2 space-y-2">
        {tool.description && (
          <p className="text-[10px] text-secondary-foreground leading-relaxed whitespace-pre-wrap">
            {tool.description}
          </p>
        )}
        {tool.max_uses !== undefined && (
          <div className="text-[9px] text-amber-600">Max uses: {tool.max_uses}</div>
        )}
        {propEntries.length > 0 && (
          <div>
            <div className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Parameters</div>
            <div>
              {propEntries.map(([name, prop]) => (
                <ToolParamRow key={name} name={name} prop={prop as Record<string, unknown>} required={required.has(name)} />
              ))}
            </div>
          </div>
        )}
      </div>
    </CollapsibleSection>
  )
}

function ToolsSection({ tools }: { tools: ToolDefinition[] }) {
  return (
    <CollapsibleSection
      title="Tools"
      badge={tools.length}
      copyText={JSON.stringify(tools, null, 2)}
    >
      <div className="divide-y divide-amber-100/60">
        {tools.map((tool, i) => (
          <ToolItemView key={tool.name || i} tool={tool} />
        ))}
      </div>
    </CollapsibleSection>
  )
}

function ObservabilityPanel({
  observability,
  isLoading,
  error,
  onRefresh,
}: {
  observability: ObservabilitySnapshot | null
  isLoading: boolean
  error: string | null
  onRefresh: () => void
}) {
  const [expandSignal, setExpandSignal] = useState<ExpandSignal>({ expand: false, v: 0 })
  const allExpanded = expandSignal.expand && expandSignal.v > 0

  return (
    <div className="border-b border-amber-200 bg-amber-50/30">
      <div className="px-3 py-2">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="text-[10px] font-semibold text-amber-700 uppercase tracking-wide">
              Observability
            </div>
            {observability && (
              <div className="flex items-center gap-1.5 text-[9px] text-amber-600">
                <span className="bg-amber-100 px-1.5 py-0.5 rounded font-mono">{observability.model.split('-').slice(1, 3).join('-')}</span>
                <span>{observability.stats.sourceMessageCount} msgs</span>
                <span>{observability.stats.replayMessageCount} replay</span>
                <span>{observability.stats.clipCount} clips</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1">
            {observability && (
              <button
                onClick={() => setExpandSignal(s => ({ expand: !s.expand, v: s.v + 1 }))}
                className="px-1.5 py-0.5 text-[9px] font-medium text-amber-700 hover:text-amber-800 hover:bg-amber-100 rounded transition-colors"
              >
                {allExpanded ? 'Collapse All' : 'Expand All'}
              </button>
            )}
            <button
              onClick={onRefresh}
              disabled={isLoading}
              className="p-1 text-amber-700 hover:text-amber-800 hover:bg-amber-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              title="Refresh"
            >
              <RefreshCw size={12} className={isLoading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-2 flex items-center gap-1.5 rounded border border-destructive/20 bg-destructive/10 px-2 py-1.5 text-[11px] text-destructive">
            <AlertCircle size={12} className="shrink-0" />
            <span className="wrap-break-word">{error}</span>
          </div>
        )}

        {isLoading && !observability && (
          <div className="flex items-center gap-2 text-[11px] text-amber-700 py-4 justify-center">
            <Loader2 size={14} className="animate-spin" />
            Loading snapshot...
          </div>
        )}

        {observability && (
          <ExpandAllCtx.Provider value={expandSignal}>
            <div className="space-y-1.5">
              <CollapsibleSection
                title="System Prompt"
                badge={`${(observability.systemPrompt.length / 1000).toFixed(1)}k`}
                copyText={observability.systemPrompt}
              >
                <SystemPromptView prompt={observability.systemPrompt} />
              </CollapsibleSection>

              {observability.tools && observability.tools.length > 0 && (
                <ToolsSection tools={observability.tools} />
              )}

              <CollapsibleSection
                title="Context"
                badge={`${(observability.contextMessage.length / 1000).toFixed(1)}k`}
                copyText={observability.contextMessage}
              >
                <ContextMessageView context={observability.contextMessage} />
              </CollapsibleSection>

              <ReplayMessagesSection replayMessages={observability.replayMessages} />

              <CollapsibleSection
                title="Source Conversation"
                badge={observability.stats.sourceMessageCount}
                copyText={JSON.stringify(observability.sourceMessages, null, 2)}
              >
                <div className="divide-y divide-border">
                  {observability.sourceMessages.map((msg, i) => (
                    <SourceMessageItem key={msg.id || i} msg={msg} index={i} />
                  ))}
                  {observability.sourceMessages.length === 0 && (
                    <div className="text-[10.5px] text-muted-foreground italic p-2.5">No source messages</div>
                  )}
                </div>
              </CollapsibleSection>
            </div>
          </ExpandAllCtx.Provider>
        )}
      </div>
    </div>
  )
}

export function ActivityTimeline({ messages, clips, workflowId, isAdmin = false }: ActivityTimelineProps) {
  const [showEvents, setShowEvents] = useState(false)
  const [showObservability, setShowObservability] = useState(false)
  const [observability, setObservability] = useState<ObservabilitySnapshot | null>(null)
  const [isObservabilityLoading, setIsObservabilityLoading] = useState(false)
  const [observabilityError, setObservabilityError] = useState<string | null>(null)

  const events = useMemo(
    () => buildActivityEvents(messages || [], clips || []),
    [messages, clips]
  )

  // Server history (unified: agent + user entries interleaved)
  const serverHistory = useQuery(
    api.agentHistory.list,
    workflowId ? { workflowId } : "skip"
  )
  const markUndone = useMutation(api.agentHistory.undo)
  const markRedone = useMutation(api.agentHistory.redo)

  // Bridge server entries into client store (local-first undo/redo)
  const bridgeServerEntries = useProjectStore((s) => s.bridgeServerEntries)
  useEffect(() => {
    if (!serverHistory) return
    bridgeServerEntries(serverHistory.map((e) => ({
      id: e.id,
      label: e.label,
      source: e.source as 'user' | 'agent',
      undoOps: e.undoOps,
      undone: e.undone,
      invalidatedAt: e.invalidatedAt,
      createdAt: e.createdAt,
    })))
  }, [serverHistory, bridgeServerEntries])

  // Local-first undo/redo via project store
  const storeUndoStack = useProjectStore((s) => s._undoStack)
  const storeRedoStack = useProjectStore((s) => s._redoStack)
  const canUndo = useProjectStore((s) => s.canUndo())
  const canRedo = useProjectStore((s) => s.canRedo())
  const storeUndo = useProjectStore((s) => s.undo)
  const storeRedo = useProjectStore((s) => s.redo)

  // Display from server data: the full activity log (every recorded action)
  // plus the reversible subset that drives undo/redo navigation.
  const { activityLog, undoStack, redoStack } = useMemo(() => {
    if (!serverHistory) {
      // Fall back to store data before server loads (undoable edits only)
      const undo = storeUndoStack.map((e): HistoryEntry => ({ id: e.id, label: e.label, source: e.source, toolName: 'edit', undone: false, undoable: true, createdAt: e.timestamp }))
      const redo = storeRedoStack.map((e): HistoryEntry => ({ id: e.id, label: e.label, source: e.source, toolName: 'edit', undone: true, undoable: true, createdAt: e.timestamp }))
      return { activityLog: [...undo, ...redo].sort((a, b) => a.createdAt - b.createdAt), undoStack: undo, redoStack: redo }
    }
    const entries = (serverHistory as HistoryEntry[]).filter((e) => !e.invalidatedAt)
    const reversible = entries.filter((e) => e.undoable !== false)
    return {
      activityLog: entries,
      undoStack: reversible.filter((e) => !e.undone),
      redoStack: reversible.filter((e) => e.undone),
    }
  }, [serverHistory, storeUndoStack, storeRedoStack])

  // Undo/redo: local-first via store, then sync server flag in background
  const handleUndo = useCallback(() => {
    if (!canUndo) return
    storeUndo()
    // Fire-and-forget: sync the undone flag on the server
    if (workflowId) void markUndone({ workflowId }).catch(() => {})
  }, [canUndo, storeUndo, workflowId, markUndone])

  const handleRedo = useCallback(() => {
    if (!canRedo) return
    storeRedo()
    if (workflowId) void markRedone({ workflowId }).catch(() => {})
  }, [canRedo, storeRedo, workflowId, markRedone])

  const getObservabilitySnapshot = useAction((api as any).workflowAgent.getAgentObservability)

  const loadObservability = useCallback(async () => {
    if (!workflowId || !isAdmin) return
    setIsObservabilityLoading(true)
    setObservabilityError(null)
    try {
      const snapshot = await getObservabilitySnapshot({ workflowId })
      setObservability(snapshot)
    } catch (error) {
      setObservabilityError(getObservabilityErrorMessage(error))
    } finally {
      setIsObservabilityLoading(false)
    }
  }, [workflowId, isAdmin, getObservabilitySnapshot])

  useEffect(() => {
    if (!showObservability) return
    void loadObservability()
  }, [showObservability, loadObservability])

  const hasHistory = undoStack.length > 0 || redoStack.length > 0

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-3 py-2 border-b border-border bg-secondary shrink-0">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-semibold text-secondary-foreground uppercase tracking-wide">History</h3>
          <div className="flex items-center gap-1">
            {isAdmin && workflowId && (
              <button
                onClick={() => setShowObservability(!showObservability)}
                className={`p-1 rounded transition-colors ${
                  showObservability ? 'bg-amber-100 text-amber-700' : 'text-muted-foreground hover:text-secondary-foreground hover:bg-muted'
                }`}
                title="Toggle full observability"
              >
                <Eye size={14} />
              </button>
            )}
            <button
              onClick={handleUndo}
              disabled={!canUndo}
              className="p-1 text-muted-foreground hover:text-secondary-foreground hover:bg-muted rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Undo (Cmd+Z)"
            >
              <Undo2 size={14} />
            </button>
            <button
              onClick={handleRedo}
              disabled={!canRedo}
              className="p-1 text-muted-foreground hover:text-secondary-foreground hover:bg-muted rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Redo (Cmd+Shift+Z)"
            >
              <Redo2 size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {showObservability && isAdmin && (
          <ObservabilityPanel
            observability={observability}
            isLoading={isObservabilityLoading}
            error={observabilityError}
            onRefresh={() => void loadObservability()}
          />
        )}

        {/* Full activity log — every recorded action, newest first */}
        {hasHistory && (
          <UnifiedHistoryTimeline
            activityLog={activityLog}
            undoStack={undoStack}
            redoStack={redoStack}
            onUndo={handleUndo}
            onRedo={handleRedo}
          />
        )}

        {/* Per-tool-call detail derived from chat (collapsed; richer for older projects) */}
        {events.length > 0 && (
          <div>
            <button
              onClick={() => setShowEvents(!showEvents)}
              className="w-full flex items-center gap-2 px-3 py-2 text-[11px] text-muted-foreground hover:text-secondary-foreground hover:bg-muted/30 transition-colors border-t border-border"
            >
              <ChevronRight size={12} className={`transition-transform ${showEvents ? 'rotate-90' : ''}`} />
              <Wrench size={11} />
              <span>Tool call details ({events.length})</span>
            </button>
            {showEvents && (
              <div className="divide-y divide-border">
                {events.map((event) => (
                  <ActivityEventItem key={event.id} event={event} />
                ))}
              </div>
            )}
          </div>
        )}

        {!hasHistory && events.length === 0 && (
          <div className="text-center py-8 px-4">
            <Clock size={24} className="mx-auto text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground">No history yet</p>
            <p className="text-xs text-muted-foreground/70 mt-1">Actions will appear here as you and the agent work</p>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Relative time formatting ─────────────────────────────────

function relativeTimeShort(ts: number): string {
  const diff = Date.now() - ts
  const s = Math.floor(diff / 1000)
  if (s < 5) return 'now'
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h`
  const d = Math.floor(h / 24)
  return `${d}d`
}

// ── Unified History Timeline ─────────────────────────────────
// Most recent at top, scroll down to go back in time.
// "Now" at top → recent edits below → oldest edits at bottom.
// Click any entry to jump to that point (instant, local-first).

function UnifiedHistoryTimeline({
  activityLog,
  undoStack,
  redoStack,
  onUndo,
  onRedo,
}: {
  activityLog: HistoryEntry[]
  undoStack: HistoryEntry[]
  redoStack: HistoryEntry[]
  onUndo: () => void
  onRedo: () => void
}) {
  const currentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    currentRef.current?.scrollIntoView({ behavior: 'instant', block: 'nearest' })
  }, [undoStack.length, redoStack.length])

  // Redo branch: reversible actions currently undone (reachable by redo), oldest→newest
  const redoEntries = redoStack

  // Applied feed: everything NOT currently undone — reversible-applied actions plus
  // every log-only/generation entry — newest first.
  const appliedNewestFirst = useMemo(
    () => [...activityLog.filter((e) => !(e.undoable !== false && e.undone))].reverse(),
    [activityLog]
  )

  // How many undo steps to reach the state *before* a given reversible row
  // (count of reversible, non-failed rows from the top of the feed down to it).
  const undoStepsById = useMemo(() => {
    const steps: Record<string, number> = {}
    let count = 0
    for (const e of appliedNewestFirst) {
      if (e.undoable !== false && e.status !== 'failed') {
        count += 1
        steps[e.id] = count
      }
    }
    return steps
  }, [appliedNewestFirst])

  const hasAnyEntries = appliedNewestFirst.length > 0 || redoEntries.length > 0

  return (
    <div className="border-b border-border">
      {/* Redo entries above "Now" (things we've undone — click to redo) */}
      {redoEntries.map((entry, i) => (
        <button
          key={entry.id}
          onClick={() => { for (let s = 0; s < redoEntries.length - i; s++) onRedo() }}
          title={`Redo to "${entry.label}"`}
          className="group w-full flex items-stretch gap-0 hover:bg-muted/20 transition-colors text-left"
        >
          <div className="w-7 flex flex-col items-center shrink-0">
            {i > 0 ? <div className="w-px flex-1 bg-border/25" /> : <div className="flex-1" />}
            <div className="w-[5px] h-[5px] rounded-full shrink-0 bg-muted-foreground/20 group-hover:bg-muted-foreground/40" />
            <div className="w-px flex-1 bg-border/25" />
          </div>
          <div className="flex-1 min-w-0 py-[5px] pr-3 flex items-baseline gap-1.5 opacity-30 group-hover:opacity-60 transition-opacity">
            <span className="text-[11px] text-muted-foreground truncate leading-tight flex-1">
              {entry.label}
            </span>
            <span className="text-[10px] text-muted-foreground/50 shrink-0 tabular-nums">
              {relativeTimeShort(entry.createdAt)}
            </span>
          </div>
        </button>
      ))}

      {/* "Now" marker */}
      {hasAnyEntries && (
        <div ref={currentRef} className="flex items-stretch gap-0 bg-foreground/[0.03]">
          <div className="w-7 flex flex-col items-center shrink-0">
            {redoEntries.length > 0 ? <div className="w-px flex-1 bg-border/25" /> : <div className="flex-1" />}
            <div className="w-2.5 h-2.5 rounded-full bg-foreground shrink-0 ring-2 ring-background" />
            {appliedNewestFirst.length > 0 ? <div className="w-px flex-1 bg-border" /> : <div className="flex-1" />}
          </div>
          <div className="flex-1 py-[5px] pr-3">
            <span className="text-[11px] font-medium text-foreground">Now</span>
          </div>
        </div>
      )}

      {/* Applied feed below "Now" (newest first). Reversible rows are clickable to
          jump back; generation/log-only and failed rows are shown but not reversible. */}
      {appliedNewestFirst.map((entry, i) => {
        const isLast = i === appliedNewestFirst.length - 1
        const failed = entry.status === 'failed'
        const reversible = entry.undoable !== false && !failed
        const steps = undoStepsById[entry.id]
        const dotColor = failed
          ? 'bg-destructive/70'
          : reversible
            ? (entry.source === 'agent' ? 'bg-blue-400' : 'bg-foreground/70')
            : 'bg-muted-foreground/30'
        const rowClass = `group w-full flex items-stretch gap-0 transition-colors text-left ${reversible ? 'hover:bg-muted/40 cursor-pointer' : 'cursor-default'}`
        const inner = (
          <>
            <div className="w-7 flex flex-col items-center shrink-0">
              <div className="w-px flex-1 bg-border" />
              <div className={`w-[7px] h-[7px] rounded-full shrink-0 ${dotColor}`} />
              {!isLast ? <div className="w-px flex-1 bg-border" /> : <div className="flex-1" />}
            </div>
            <div className="flex-1 min-w-0 py-[5px] pr-3 flex items-baseline gap-1.5">
              <span className={`text-[11px] truncate leading-tight flex-1 ${failed ? 'text-muted-foreground line-through decoration-destructive/40' : 'text-secondary-foreground'}`}>
                {entry.label}
              </span>
              {failed && <span className="text-[9px] text-destructive/70 shrink-0">failed</span>}
              {!failed && !reversible && <span className="text-[9px] text-muted-foreground/50 shrink-0">log</span>}
              {entry.source === 'agent' && <span className="text-[9px] text-blue-400 shrink-0">ai</span>}
              <span className="text-[10px] text-muted-foreground/50 shrink-0 tabular-nums">
                {relativeTimeShort(entry.createdAt)}
              </span>
            </div>
          </>
        )
        return reversible ? (
          <button
            key={entry.id}
            onClick={() => { for (let s = 0; s < (steps ?? 0); s++) onUndo() }}
            title={`Go back to before "${entry.label}"`}
            className={rowClass}
          >
            {inner}
          </button>
        ) : (
          <div key={entry.id} className={rowClass} title={entry.label}>
            {inner}
          </div>
        )
      })}

    </div>
  )
}
